import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { clearAuth, getCurrentAdmin } from "../lib/auth.js";
import { Logo } from "./Logo.js";

/**
 * 管理后台的壳:
 *   - 左侧固定导航 + logo
 *   - 顶部薄 bar:显示当前 admin + 登出
 *   - 右侧 Outlet 承载页面
 */
export function AppShell() {
  const me = getCurrentAdmin();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const nav = [
    { to: "/", label: "Dashboard", end: true, icon: "▣" },
    { to: "/packages", label: "包管理", icon: "◫" },
    { to: "/review", label: "审核队列", icon: "✓" },
    { to: "/admins", label: "管理员", icon: "◉", ownerOnly: true },
    { to: "/audit", label: "审计日志", icon: "≡" },
  ].filter((n) => !n.ownerOnly || me?.role === "owner");

  const logout = () => {
    clearAuth();
    navigate("/login", { replace: true });
  };

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "var(--bg)" }}>
      <aside style={styles.sidebar}>
        <div style={styles.brand}>
          <Logo size={34} radius={10} />
          <div>
            <div style={styles.brandName}>mhwork-hub</div>
            <div style={styles.brandSub}>admin console</div>
          </div>
        </div>

        <nav style={styles.nav}>
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              style={({ isActive }) => ({
                ...styles.navItem,
                ...(isActive ? styles.navItemActive : {}),
              })}
            >
              <span style={styles.navIcon}>{n.icon}</span>
              {n.label}
            </NavLink>
          ))}
        </nav>
      </aside>

      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <header style={styles.topbar}>
          <div style={{ flex: 1 }} />
          <div style={{ position: "relative" }}>
            <button
              style={styles.userBtn}
              onClick={() => setMenuOpen((v) => !v)}
              onBlur={() => setTimeout(() => setMenuOpen(false), 120)}
            >
              <div
                style={{
                  ...styles.userAvatar,
                  background: "var(--primary)",
                }}
              >
                {(me?.displayName || me?.email || "?")[0]!.toUpperCase()}
              </div>
              <div style={{ textAlign: "left", minWidth: 0 }}>
                <div style={styles.userName}>{me?.displayName || "管理员"}</div>
                <div style={styles.userRole}>{me?.role}</div>
              </div>
              <span style={{ color: "var(--text-muted)", fontSize: 10 }}>▾</span>
            </button>
            {menuOpen && (
              <div style={styles.menu}>
                <div style={styles.menuEmail}>{me?.email}</div>
                <div style={styles.menuDivider} />
                <button style={styles.menuItem} onClick={logout}>
                  <span style={{ color: "var(--danger)" }}>↪</span> 登出
                </button>
              </div>
            )}
          </div>
        </header>

        <main style={styles.content}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  sidebar: {
    width: 220,
    flexShrink: 0,
    background: "var(--surface)",
    borderRight: "1px solid var(--border)",
    display: "flex",
    flexDirection: "column",
    gap: 8,
    padding: "18px 12px",
  },
  brand: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "4px 8px 16px",
    borderBottom: "1px solid var(--border-soft)",
  },
  brandName: {
    fontSize: 14,
    fontWeight: 600,
    letterSpacing: "-0.01em",
  },
  brandSub: {
    fontSize: 10,
    color: "var(--text-muted)",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  },
  nav: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    marginTop: 8,
  },
  navItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "8px 12px",
    borderRadius: "var(--radius)",
    textDecoration: "none",
    color: "var(--text-muted)",
    fontSize: 13,
    fontWeight: 500,
    transition: "all .12s",
  },
  navItemActive: {
    background: "var(--primary-soft)",
    color: "var(--primary)",
  },
  navIcon: {
    fontSize: 14,
    width: 16,
    display: "inline-flex",
    justifyContent: "center",
  },
  topbar: {
    height: 56,
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "0 24px",
    background: "var(--surface)",
    borderBottom: "1px solid var(--border)",
  },
  userBtn: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "4px 10px 4px 4px",
    borderRadius: 20,
    border: "1px solid var(--border)",
    background: "var(--surface)",
    cursor: "pointer",
    transition: "background .12s, border-color .12s",
  },
  userAvatar: {
    width: 30,
    height: 30,
    borderRadius: "50%",
    color: "#fff",
    fontSize: 13,
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  userName: { fontSize: 12, fontWeight: 500, lineHeight: 1.2 },
  userRole: {
    fontSize: 10,
    color: "var(--text-muted)",
    lineHeight: 1.2,
    marginTop: 1,
  },
  menu: {
    position: "absolute",
    top: "calc(100% + 6px)",
    right: 0,
    width: 200,
    background: "var(--surface)",
    borderRadius: "var(--radius-lg)",
    border: "1px solid var(--border)",
    boxShadow: "var(--shadow-md)",
    padding: 4,
    zIndex: 50,
  },
  menuEmail: {
    fontSize: 11,
    color: "var(--text-muted)",
    padding: "8px 10px 4px",
    fontFamily: "var(--font-mono)",
  },
  menuDivider: {
    height: 1,
    background: "var(--border-soft)",
    margin: "4px 0",
  },
  menuItem: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    width: "100%",
    padding: "8px 10px",
    background: "none",
    border: 0,
    borderRadius: "var(--radius-sm)",
    fontSize: 13,
    color: "var(--text)",
    cursor: "pointer",
    textAlign: "left",
  },
  content: {
    flex: 1,
    padding: 28,
    overflow: "auto",
    minWidth: 0,
  },
};
